/**
 * useAdminReportData - Custom hook for admin report management
 *
 * Composes the admin-report domain hooks and coordinates the initial load.
 * Owns core report fields (content, reporter, closed state, images) and
 * delegates the rest:
 * - useReportTags: tag updates (KPIs, HROs, Locations) and AI override
 * - useAdminComments: comment CRUD
 * - useReportEscalation: escalation/reminder state and actions
 * - useTicketStatus: status, assignment, analysts
 * - useTicketWorkItems: todos and attachments
 *
 * @param {string} uuid - The report UUID
 * @param {Object} options - Configuration options
 * @param {string} options.defaultOrganization - Fallback organization
 * @param {Function} options.onNavigateToLogin - Callback for auth redirect
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

import Util from '../util/util';
import { showSuccess } from '../util/error/hookErrors';
import { t } from '../i18n';
import logger from '../util/logger';
import { addChangedReport, removeChangedReport } from '../util/storage';
import { trackEngagementAsync } from '../util/engagementTracker';

import useReportEscalation from './useReportEscalation';
import useReportTags from './useReportTags';
import useAdminComments from './useAdminComments';
import useTicketStatus from './useTicketStatus';
import useTicketWorkItems from './useTicketWorkItems';

const log = logger.module('AdminReportData');

const useAdminReportData = (uuid, options = {}) => {
  const { defaultOrganization = '' } = options;

  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Basic report fields
  const [organization, setOrganization] = useState(defaultOrganization);
  const [timestamp, setTimestamp] = useState(null);
  const [content, setContent] = useState('');
  const [reporter, setReporter] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [closedDate, setClosedDate] = useState(null);

  // Images
  const [images, setImages] = useState([]);
  const [imageURLs, setImageURLs] = useState([]);

  // UI state
  const [isEditing, setIsEditing] = useState(false);

  // Activity log
  const [activityLog, setActivityLog] = useState([]);

  /**
   * Mark a report as changed in AsyncStorage
   */
  const markReportChanged = useCallback(
    async (changed = true) => {
      try {
        if (changed) {
          await addChangedReport(uuid);
        } else {
          await removeChangedReport(uuid);
        }
      } catch (err) {
        log.error('Failed to mark report as changed', { error: err.message });
      }
    },
    [uuid]
  );

  /**
   * Fetch report from API
   */
  const fetchReport = useCallback(async () => {
    if (!uuid) return null;

    try {
      const data = await Util.get('Resty', '/items/getFromUuid/' + uuid);

      // Track report view for surprise badges (shows toast if badge earned)
      trackEngagementAsync('report_view');

      return data.items;
    } catch (err) {
      Util.printAPIError(err);
      setError(err);
      return null;
    }
  }, [uuid]);

  // Domain hooks
  const escalation = useReportEscalation(uuid, timestamp);
  const { clearEscalationState } = escalation;

  const tags = useReportTags(uuid, timestamp, { markReportChanged });

  const adminComments = useAdminComments(uuid, timestamp, {
    fetchReport,
    clearEscalationState,
  });

  const ticketStatus = useTicketStatus(uuid, timestamp, {
    organization,
    fetchReport,
    clearEscalationState,
    setIsClosed,
    setClosedDate,
  });
  const { fetchAnalysts } = ticketStatus;

  const workItems = useTicketWorkItems(uuid, timestamp, { clearEscalationState });

  /**
   * Close or reopen the report
   */
  const closeOrReopenReport = useCallback(async () => {
    const requestData = {
      queryParams: {
        uuid: uuid,
        timestamp: timestamp,
        reportId: Util.formatUuid(uuid),
        isClosed: !isClosed,
      },
    };

    try {
      await Util.patch('Resty', '/items/close', requestData);
      clearEscalationState();
      await Util.setReportCache(uuid, 'reportChanged', true);
      await Util.setReportCache(uuid, 'reportClosed', !isClosed);
      await Util.setReportCache(uuid, 'reportReopened', isClosed);
      setIsClosed(!isClosed);
      setClosedDate(!isClosed ? Date.now() : null);
      showSuccess(t(isClosed ? 'reports:ticket.reportReopened' : 'reports:ticket.reportClosed'));

      // Track close/reopen for surprise badges (shows toast if badge earned)
      trackEngagementAsync(isClosed ? 'report_reopened' : 'report_closed');
    } catch (err) {
      Util.handleApiError(err, {
        userMessage: t(isClosed ? 'reports:ticket.reopenFailed' : 'reports:ticket.closeFailed'),
      });
    }
  }, [uuid, timestamp, isClosed, clearEscalationState]);

  /**
   * Fetch signed URLs for images
   */
  const fetchImageURLs = useCallback(async (imageIds) => {
    if (!imageIds || imageIds.length === 0) {
      setImageURLs([]);
      return [];
    }

    // Pass the report uuid so the server can verify access to this report's images
    const promises = imageIds.map((id) => Util.getImageURL(id, false, uuid));
    const urls = await Promise.all(promises);
    setImageURLs(urls);
    return urls;
  }, [uuid]);

  // Initial load: fetch report by UUID
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        const report = await fetchReport();
        if (!report || !isMounted) return;

        // Basic fields
        setOrganization(report.organization || defaultOrganization);
        setTimestamp(report.timestamp);
        setContent(report.content || '');
        setReporter(report.reporter || '');
        setIsUrgent(report.isUrgent === true);
        setIsClosed(report.isClosed === true);
        setClosedDate(report.closedDate || null);

        // Images
        if (Array.isArray(report.images)) {
          setImages(report.images);
        } else {
          setImages([]);
        }

        // Activity log
        setActivityLog(Array.isArray(report.activityLog) ? report.activityLog : []);

        // Domain state
        escalation.initializeEscalation(report);
        tags.initializeTags(report);
        adminComments.initializeComments(report);
        ticketStatus.initializeTicketStatus(report);
        workItems.initializeWorkItems(report);

        // Fetch analysts for assignment dropdown
        if (report.organization) {
          fetchAnalysts();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialize* callbacks are stable
  }, [uuid, defaultOrganization, fetchReport, fetchAnalysts]);

  // Effect: Fetch image URLs when images change
  useEffect(() => {
    if (images && images.length > 0) {
      fetchImageURLs(images);
    } else {
      setImageURLs([]);
    }
  }, [images, fetchImageURLs]);

  // Calculate last updated timestamp
  const lastUpdated = useMemo(() => {
    const timestamps = [
      timestamp, // Report creation
      ticketStatus.assignedAt,
      closedDate,
      ...adminComments.comments.map((c) => c.commentDate),
      ...workItems.todos.map((t) => t.completedAt || t.createdAt).filter(Boolean),
      ...workItems.attachments.map((a) => a.uploadedAt).filter(Boolean),
      ...ticketStatus.statusHistory.map((s) => s.changedAt),
    ].filter(Boolean);

    return timestamps.length > 0 ? Math.max(...timestamps) : timestamp;
  }, [
    timestamp,
    ticketStatus.assignedAt,
    closedDate,
    adminComments.comments,
    workItems.todos,
    workItems.attachments,
    ticketStatus.statusHistory,
  ]);

  return {
    // Loading state
    loading,
    error,

    // Basic fields
    organization,
    content,
    timestamp,
    reporter,
    isUrgent,
    isClosed,
    closedDate,

    // Images
    images,
    imageURLs,
    setImageURLs,

    // Comments
    comments: adminComments.comments,
    setComments: adminComments.setComments,
    commentsProp: adminComments.commentsProp,

    // Tags with setters
    kpis: tags.kpis,
    setKpis: tags.setKpis,
    hros: tags.hros,
    setHros: tags.setHros,
    locations: tags.locations,
    setLocations: tags.setLocations,

    // AI fields - separate override states
    aiOverridden: tags.aiOverridden, // Legacy combined override (for backwards compat)
    aiKpiOverridden: tags.aiKpiOverridden,
    aiHroOverridden: tags.aiHroOverridden,
    aiKpis: tags.aiKpis,
    aiHros: tags.aiHros,

    // UI state
    isEditing,
    setIsEditing,

    // Ticketing fields
    status: ticketStatus.status,
    statusUpdating: ticketStatus.statusUpdating,
    statusHistory: ticketStatus.statusHistory,
    assignedTo: ticketStatus.assignedTo,
    assignedAt: ticketStatus.assignedAt,
    assignedBy: ticketStatus.assignedBy,
    escalationLevel: escalation.escalationLevel,
    escalatedAt: escalation.escalatedAt,
    nextEscalationAt: escalation.nextEscalationAt,
    escalationWindowMs: escalation.escalationWindowMs,
    nextReminderAt: escalation.nextReminderAt,
    reminderWindowMs: escalation.reminderWindowMs,
    remindersMuted: escalation.remindersMuted,
    remindersCapped: escalation.remindersCapped,
    todos: workItems.todos,
    attachments: workItems.attachments,
    analysts: ticketStatus.analysts,
    analystsLoading: ticketStatus.analystsLoading,
    activityLog,
    lastUpdated,

    // Tag update triggers
    triggerKpisUpdate: tags.triggerKpisUpdate,
    triggerHrosUpdate: tags.triggerHrosUpdate,
    triggerLocationsUpdate: tags.triggerLocationsUpdate,

    // Actions
    refetch: fetchReport,
    overrideAI: tags.overrideAI,
    restoreAI: tags.restoreAI,
    closeOrReopenReport,
    addComment: adminComments.addComment,
    editComment: adminComments.editComment,
    deleteComment: adminComments.deleteComment,
    markReportChanged,

    // Ticketing actions
    updateStatus: ticketStatus.updateStatus,
    updateAssignment: ticketStatus.updateAssignment,
    escalateTicket: escalation.escalateTicket,
    toggleRemindersMuted: escalation.toggleRemindersMuted,
    addTodo: workItems.addTodo,
    toggleTodo: workItems.toggleTodo,
    deleteTodo: workItems.deleteTodo,
    uploadAttachment: workItems.uploadAttachment,
    downloadAttachment: workItems.downloadAttachment,
    deleteAttachment: workItems.deleteAttachment,
    fetchAnalysts: ticketStatus.fetchAnalysts,
  };
};

export default useAdminReportData;
